<template>
  <section v-if="error">
    <br />
    <p class="error" v-html="error"></p>
  </section>

  <section class="grid-container" v-if="!error && loaded">
    <div class="grid-container wizard">
      <div class="grid-x grid-margin-x">
        <WizardProgress
          :steps="wizardSteps"
          :currentStep="stepIndex"
          :finalStepIsComplete="true"
        />

        <component
          v-if="loaded"
          :key="stepIndex"
          :is="currentStepComponent"
          ref="stepComponentRef"
          :formData="formData"
          :clientInvite="clientInvite"
          @next="nextStep"
          @prev="prevStep"
          @update-form="handleFormUpdate"
        />

        <div
          v-if="stepIndex < activeSteps.length - 1"
          id="wizard-navigation"
          class="cell small-12"
        >
          <button
            class="btn btn-secondary"
            @click="prevStep"
            :disabled="isProcessing || stepIndex === 0"
          >
            Previous
          </button>

          <button
            class="btn btn-primary"
            @click="nextStep"
            :disabled="isProcessing || stepIndex === activeSteps.length - 1"
          >
            {{ isProcessing ? 'Please wait...' : 'Next' }}
          </button>

          <button class="btn btn-link" @click="finish" :disabled="isProcessing">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import WizardProgress from '@/components/navigation/WizardProgressSteps.vue'

import Step1 from './Get-Started-Step1.vue'
import Step2 from './Get-Started-Step2.vue'
import Step3 from './Get-Started-Step3.vue'
import Step4 from './Get-Started-Step4.vue'
import Step5 from './Get-Started-Step5.vue'
import Step6 from './Get-Started-Step6.vue'

import { getClientInvite } from '@/services/implementationmanagement.api'
import { useIdentityStore } from '@/stores/identity'

const route = useRoute()
const signupId = route.params.signupId
const identity = useIdentityStore()

const error = ref(null)
const clientInvite = ref(null)
const loaded = ref(false)
const stepIndex = ref(0)
const stepComponentRef = ref(null)
const isProcessing = ref(false)

const steps = [
  {
    label: 'Find Your Company',
    component: Step1
  },
  {
    label: 'Company Information',
    component: Step2
  },
  {
    label: 'Identity Verification',
    component: Step3
  },
  {
    label: 'Contract',
    component: Step4
  },
  {
    label: 'Direct Debit Mandate',
    component: Step5
  },
  {
    label: 'Complete',
    component: Step6
  }
]

const shouldSkipInviteStep = (step) => {
  return (
    clientInvite.value?.clientType === 'IntermediaryClient' &&
    [
      'Identity Verification',
      'Contract',
      'Direct Debit Mandate'
    ].includes(step.label)
  )
}

const activeSteps = computed(() => {
  return steps.filter(step => !shouldSkipInviteStep(step))
})

const wizardSteps = computed(() => {
  return activeSteps.value.map(step => step.label)
})

const currentStepComponent = computed(() => {
  return activeSteps.value[stepIndex.value].component
})

const formData = ref({
  name: '',
  phoneNumber: '',
  companyNumber: '',
  address: {
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    postCode: '',
    country: ''
  },
  clientType: '',
  clientOnboardingType: 'DirectClientOnboarding',
  contacts: []
})

onMounted(async () => {
  try {
    if (identity.isLoggedIn) {
      error.value = 'Sorry, your invite has already been completed. Please contact your Qtac account manager.'
      return
    }

    const res = await getClientInvite(signupId)
    clientInvite.value = res.data

    formData.value.clientType = clientInvite.value.clientType

    if (clientInvite.value.status === 'Accepted') {
      error.value = 'Sorry, it looks like your invite has already been accepted. Click <a href="/">here</a> to login.'
      return
    }

    loaded.value = true
  } catch (err) {
    error.value = 'Sorry, we\'ve been unable to load your invite. Please contact your Qtac account manager.'
    console.error(err)
  }
})

const nextStep = async () => {
  if (isProcessing.value) return

  isProcessing.value = true

  try {
    if (
      stepComponentRef.value &&
      typeof stepComponentRef.value.validateStep === 'function'
    ) {
      const isValid = stepComponentRef.value.validateStep()

      if (!isValid) {
        return
      }
    }

    if (
      stepComponentRef.value &&
      typeof stepComponentRef.value.executeStep === 'function'
    ) {
      const success = await stepComponentRef.value.executeStep()

      if (!success) {
        return
      }
    }

    error.value = null

    if (stepIndex.value < activeSteps.value.length - 1) {
      stepIndex.value++
    }
  } finally {
    isProcessing.value = false
  }
}

const prevStep = () => {
  if (isProcessing.value) return

  if (stepIndex.value > 0) {
    stepIndex.value--
  }
}

const handleFormUpdate = (updatedData) => {
  formData.value = updatedData
}
</script>

<style scoped>
#wizard-breadcrumbs {
  margin-bottom: 40px;
  margin-top: 40px;
}

#wizard-navigation {
  margin-top: 30px;
}
</style>
